import { supabase } from './supabase';
import type { 
  AnalyticsDashboardData, 
  KPIMetrics, 
  UserEngagementMetrics, 
  FinancialInsights, 
  CustomerAnalytics,
  DateRange,
  DashboardFilters,
  ChartData,
  TrendData
} from '../types/analytics';

export async function getAnalyticsDashboard(filters: DashboardFilters): Promise<AnalyticsDashboardData> {
  try {
    const [kpis, engagement, financial, customer, trends] = await Promise.all([
      getKPIMetrics(filters.dateRange),
      getUserEngagementMetrics(filters.dateRange),
      getFinancialInsights(filters.dateRange),
      getCustomerAnalytics(filters.dateRange),
      getTrendData(filters.dateRange)
    ]);

    const realTimeStats = await getRealTimeStats();

    return {
      kpis,
      engagement,
      financial,
      customer,
      trends,
      realTimeStats
    };
  } catch (error) {
    console.error('Error fetching analytics dashboard:', error);
    throw error;
  }
}

async function getKPIMetrics(dateRange: DateRange): Promise<KPIMetrics> {
  try {
    // Get revenue data
    const { data: revenueData } = await supabase.rpc('get_revenue_metrics', {
      start_date: dateRange.start,
      end_date: dateRange.end
    });

    // Get user metrics
    const { data: userMetrics } = await supabase.rpc('get_user_metrics', {
      start_date: dateRange.start,
      end_date: dateRange.end
    });

    // Get conversion metrics
    const { data: conversionData } = await supabase.rpc('get_conversion_metrics', {
      start_date: dateRange.start,
      end_date: dateRange.end
    });

    return {
      total_revenue: {
        daily: revenueData?.daily_revenue || 0,
        monthly: revenueData?.monthly_revenue || 0,
        yearly: revenueData?.yearly_revenue || 0
      },
      active_users: userMetrics?.active_users || 0,
      customer_acquisition_rate: userMetrics?.acquisition_rate || 0,
      conversion_rates: {
        registry_creation: conversionData?.registry_conversion || 0,
        gift_claiming: conversionData?.claim_conversion || 0,
        payment_success: conversionData?.payment_conversion || 0
      },
      average_order_value: revenueData?.avg_order_value || 0,
      customer_lifetime_value: userMetrics?.lifetime_value || 0
    };
  } catch (error) {
    console.error('Error fetching KPI metrics:', error);
    // Return default values
    return {
      total_revenue: { daily: 0, monthly: 0, yearly: 0 },
      active_users: 0,
      customer_acquisition_rate: 0,
      conversion_rates: { registry_creation: 0, gift_claiming: 0, payment_success: 0 },
      average_order_value: 0,
      customer_lifetime_value: 0
    };
  }
}

async function getUserEngagementMetrics(dateRange: DateRange): Promise<UserEngagementMetrics> {
  try {
    const { data: engagementData } = await supabase.rpc('get_engagement_metrics', {
      start_date: dateRange.start,
      end_date: dateRange.end
    });

    return {
      daily_active_users: engagementData?.dau || 0,
      monthly_active_users: engagementData?.mau || 0,
      session_duration: engagementData?.avg_session_duration || 0,
      bounce_rate: engagementData?.bounce_rate || 0,
      retention_rates: {
        day_1: engagementData?.retention_1d || 0,
        day_7: engagementData?.retention_7d || 0,
        day_30: engagementData?.retention_30d || 0
      },
      feature_usage: {
        registry_creation: engagementData?.registry_usage || 0,
        gift_claiming: engagementData?.claim_usage || 0,
        group_gifting: engagementData?.group_gift_usage || 0,
        rsvp_responses: engagementData?.rsvp_usage || 0
      }
    };
  } catch (error) {
    console.error('Error fetching engagement metrics:', error);
    return {
      daily_active_users: 0,
      monthly_active_users: 0,
      session_duration: 0,
      bounce_rate: 0,
      retention_rates: { day_1: 0, day_7: 0, day_30: 0 },
      feature_usage: { registry_creation: 0, gift_claiming: 0, group_gifting: 0, rsvp_responses: 0 }
    };
  }
}

async function getFinancialInsights(dateRange: DateRange): Promise<FinancialInsights> {
  try {
    const { data: financialData } = await supabase.rpc('get_financial_insights', {
      start_date: dateRange.start,
      end_date: dateRange.end
    });

    return {
      revenue_breakdown: {
        registries: financialData?.registry_revenue || 0,
        group_gifts: financialData?.group_gift_revenue || 0,
        platform_fees: financialData?.platform_fees || 0
      },
      operating_costs: financialData?.operating_costs || 0,
      profit_margins: financialData?.profit_margin || 0,
      payment_success_rate: financialData?.payment_success_rate || 0,
      refund_rate: financialData?.refund_rate || 0,
      pending_withdrawals: financialData?.pending_withdrawals || 0,
      total_withdrawn: financialData?.total_withdrawn || 0
    };
  } catch (error) {
    console.error('Error fetching financial insights:', error);
    return {
      revenue_breakdown: { registries: 0, group_gifts: 0, platform_fees: 0 },
      operating_costs: 0,
      profit_margins: 0,
      payment_success_rate: 0,
      refund_rate: 0,
      pending_withdrawals: 0,
      total_withdrawn: 0
    };
  }
}

async function getCustomerAnalytics(dateRange: DateRange): Promise<CustomerAnalytics> {
  try {
    const { data: customerData } = await supabase.rpc('get_customer_analytics', {
      start_date: dateRange.start,
      end_date: dateRange.end
    });

    return {
      segmentation: {
        new_users: customerData?.new_users || 0,
        returning_users: customerData?.returning_users || 0,
        power_users: customerData?.power_users || 0
      },
      geographic_distribution: customerData?.geographic_data || [],
      demographics: {
        age_groups: customerData?.age_groups || [],
        gender_distribution: customerData?.gender_data || []
      }
    };
  } catch (error) {
    console.error('Error fetching customer analytics:', error);
    return {
      segmentation: { new_users: 0, returning_users: 0, power_users: 0 },
      geographic_distribution: [],
      demographics: { age_groups: [], gender_distribution: [] }
    };
  }
}

async function getTrendData(dateRange: DateRange): Promise<{ revenue: TrendData[]; users: TrendData[]; conversions: TrendData[] }> {
  try {
    const { data: trendData } = await supabase.rpc('get_trend_data', {
      start_date: dateRange.start,
      end_date: dateRange.end
    });

    return {
      revenue: trendData?.revenue_trends || [],
      users: trendData?.user_trends || [],
      conversions: trendData?.conversion_trends || []
    };
  } catch (error) {
    console.error('Error fetching trend data:', error);
    return { revenue: [], users: [], conversions: [] };
  }
}

async function getRealTimeStats() {
  try {
    const { data: realTimeData } = await supabase.rpc('get_realtime_stats');

    return {
      activeUsers: realTimeData?.active_users || 0,
      recentTransactions: realTimeData?.recent_transactions || 0,
      systemHealth: (realTimeData?.system_health || 'good') as 'good' | 'warning' | 'critical'
    };
  } catch (error) {
    console.error('Error fetching real-time stats:', error);
    return {
      activeUsers: 0,
      recentTransactions: 0,
      systemHealth: 'good' as const
    };
  }
}

export function generateChartData(data: any[], labels: string[], dataKey: string): ChartData {
  return {
    labels,
    datasets: [{
      label: 'Data',
      data: data.map(item => item[dataKey] || 0),
      backgroundColor: 'rgba(0, 48, 73, 0.1)',
      borderColor: '#003049',
      fill: true
    }]
  };
}

export function calculateGrowthRate(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
}

export function formatCurrency(amount: number, currency: string = 'LKR'): string {
  return new Intl.NumberFormat('en-LK', {
    style: 'currency',
    currency: currency === 'LKR' ? 'LKR' : 'USD',
    minimumFractionDigits: 2
  }).format(amount);
}

export function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`;
}

export function exportAnalyticsData(data: AnalyticsDashboardData, filename: string): void {
  const csvContent = [
    ['Metric', 'Value'],
    ['Daily Revenue', data.kpis.total_revenue.daily],
    ['Monthly Revenue', data.kpis.total_revenue.monthly],
    ['Yearly Revenue', data.kpis.total_revenue.yearly],
    ['Active Users', data.kpis.active_users],
    ['Customer Acquisition Rate', data.kpis.customer_acquisition_rate],
    ['Average Order Value', data.kpis.average_order_value],
    ['Daily Active Users', data.engagement.daily_active_users],
    ['Monthly Active Users', data.engagement.monthly_active_users],
    ['Session Duration', data.engagement.session_duration],
    ['Bounce Rate', data.engagement.bounce_rate],
    ['Payment Success Rate', data.financial.payment_success_rate],
    ['Refund Rate', data.financial.refund_rate],
    ['Profit Margin', data.financial.profit_margins]
  ].map(row => row.join(',')).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}